import type { ReservationStatus } from '@stayafrica/contracts';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChipFilter,
  Container,
  EmptyState,
  ErrorState,
  Input,
  KpiCard,
  NoPermissionState,
  PageHeader,
  StatusBadge,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
  UserCell,
} from '@stayafrica/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { BedDouble, CalendarCheck, Plus, Search, ShieldAlert, Timer, Wallet } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Can, useCan } from '@/features/auth/session';
import {
  reservationsQueryOptions,
  type ReservationsFilters,
} from '@/features/reservations/queries';
import {
  RESERVATION_FILTER_STATUSES,
  complianceTone,
  reservationTone,
} from '@/features/reservations/status';
import { resourcesQueryOptions } from '@/features/resources/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDate } from '@/lib/format';

interface ReservationsSearch {
  q?: string;
  status?: string;
}

export const Route = createFileRoute('/_app/reservations/')({
  validateSearch: (search: Record<string, unknown>): ReservationsSearch => ({
    ...(typeof search['q'] === 'string' ? { q: search['q'] } : {}),
    ...(typeof search['status'] === 'string' ? { status: search['status'] } : {}),
  }),
  component: ReservationsPage,
});

function ReservationsPage() {
  const { t, i18n } = useTranslation(['reservations', 'resources', 'common', 'errors']);
  const search = Route.useSearch();
  const canRead = useCan('reservation.read');

  const [term, setTerm] = useState(search.q ?? '');
  const [debounced, setDebounced] = useState(search.q ?? '');
  const [statuses, setStatuses] = useState<ReservationStatus[]>(
    search.status ? (search.status.split(',') as ReservationStatus[]) : [],
  );
  const [resourceId, setResourceId] = useState<string | undefined>(undefined);
  const [balanceDue, setBalanceDue] = useState(false);
  const [arrivalFrom, setArrivalFrom] = useState('');
  const [arrivalTo, setArrivalTo] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.length >= 2 ? term : ''), 250);
    return () => window.clearTimeout(timer);
  }, [term]);

  const filters: ReservationsFilters = {
    ...(debounced ? { q: debounced } : {}),
    ...(statuses.length > 0 ? { status: statuses } : {}),
    ...(resourceId ? { resourceId } : {}),
    ...(balanceDue ? { balanceDue: true } : {}),
    ...(arrivalFrom ? { arrivalFrom: new Date(`${arrivalFrom}T00:00:00`).toISOString() } : {}),
    ...(arrivalTo ? { arrivalTo: new Date(`${arrivalTo}T23:59:59`).toISOString() } : {}),
  };

  const query = useInfiniteQuery({ ...reservationsQueryOptions(filters), enabled: canRead });
  const resourcesList = useInfiniteQuery({ ...resourcesQueryOptions({}), enabled: canRead });

  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const resources = resourcesList.data?.pages.flatMap((page) => page.data) ?? [];
  const active = rows.filter((row) => ['OPTION', 'CONFIRMED', 'CHECKED_IN'].includes(row.status));
  const options = rows.filter((row) => row.status === 'OPTION');
  const totalBalance = rows.reduce((sum, row) => sum + Math.max(row.totals.balance, 0), 0);
  const incomplete = rows.filter((row) => row.compliance.status === 'INCOMPLETE');

  const toggleStatus = (status: ReservationStatus) =>
    setStatuses((current) =>
      current.includes(status) ? current.filter((value) => value !== status) : [...current, status],
    );

  const hasFilters =
    debounced !== '' ||
    statuses.length > 0 ||
    Boolean(resourceId) ||
    balanceDue ||
    arrivalFrom !== '';

  return (
    <Container>
      <PageHeader
        title={t('reservations:title')}
        subtitle={t('reservations:subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('reservations:title') },
        ]}
        actions={
          <Can permission="reservation.create">
            <Button variant="primary" asChild>
              <Link to="/reservations/new">
                <Plus className="ico" aria-hidden="true" />
                {t('reservations:create.action')}
              </Link>
            </Button>
          </Can>
        }
      />

      <div className="stack">
        <div className="grid grid-4">
          <KpiCard
            tone="violet"
            icon={<CalendarCheck className="ico ico-16" aria-hidden="true" />}
            label={t('reservations:kpi.active')}
            value={active.length}
            loading={query.isPending}
            footer={t('reservations:kpi.activeFoot')}
          />
          <KpiCard
            tone="amber"
            icon={<Timer className="ico ico-16" aria-hidden="true" />}
            label={t('reservations:kpi.options')}
            value={options.length}
            loading={query.isPending}
            footer={t('reservations:kpi.optionsFoot')}
          />
          <KpiCard
            tone="blue"
            icon={<Wallet className="ico ico-16" aria-hidden="true" />}
            label={t('reservations:kpi.balance')}
            value={formatAmount(totalBalance, i18n.language)}
            loading={query.isPending}
            footer={t('reservations:kpi.balanceFoot')}
          />
          <KpiCard
            tone="red"
            icon={<ShieldAlert className="ico ico-16" aria-hidden="true" />}
            label={t('reservations:kpi.compliance')}
            value={incomplete.length}
            loading={query.isPending}
            footer={t('reservations:kpi.complianceFoot')}
          />
        </div>

        <Card>
          <CardHeader
            title={t('reservations:listTitle')}
            icon={<BedDouble className="ico ico-16" aria-hidden="true" />}
            actions={
              <Badge tone="neutral">{t('reservations:countLabel', { count: rows.length })}</Badge>
            }
          />

          <Toolbar>
            <Input
              type="search"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={t('reservations:searchPlaceholder')}
              aria-label={t('reservations:searchLabel')}
              icon={<Search className="ico" aria-hidden="true" />}
              style={{ width: 260, height: 32, fontSize: 'var(--fs-13)' }}
            />
            {RESERVATION_FILTER_STATUSES.map((status) => (
              <ChipFilter
                key={status}
                active={statuses.includes(status)}
                onClick={() => toggleStatus(status)}
              >
                {t(`reservations:status.${status}`)}
              </ChipFilter>
            ))}
          </Toolbar>

          <Toolbar>
            <label className="sr-only" htmlFor="filter-resource">
              {t('reservations:filters.resource')}
            </label>
            <select
              id="filter-resource"
              className="select"
              style={{ width: 'auto', height: 30, fontSize: 'var(--fs-13)' }}
              value={resourceId ?? ''}
              onChange={(event) => setResourceId(event.target.value || undefined)}
            >
              <option value="">{t('reservations:filters.allResources')}</option>
              {resources.map((resource) => (
                <option key={resource.id} value={resource.id}>
                  {resource.name}
                </option>
              ))}
            </select>

            <Input
              type="date"
              aria-label={t('reservations:filters.from')}
              value={arrivalFrom}
              onChange={(event) => setArrivalFrom(event.target.value)}
              style={{ width: 160, height: 30, fontSize: 'var(--fs-13)' }}
            />
            <Input
              type="date"
              aria-label={t('reservations:filters.to')}
              value={arrivalTo}
              onChange={(event) => setArrivalTo(event.target.value)}
              style={{ width: 160, height: 30, fontSize: 'var(--fs-13)' }}
            />

            <ChipFilter active={balanceDue} onClick={() => setBalanceDue((value) => !value)}>
              {t('reservations:filters.balanceDue')}
            </ChipFilter>

            <span className="spacer" />
            {hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTerm('');
                  setDebounced('');
                  setStatuses([]);
                  setResourceId(undefined);
                  setBalanceDue(false);
                  setArrivalFrom('');
                  setArrivalTo('');
                }}
              >
                {t('common:actions.clearFilters')}
              </Button>
            ) : null}
          </Toolbar>

          <CardBody density="flush">
            {query.isPending ? (
              <TableSkeleton rows={8} columns={7} label={t('common:states.loading')} />
            ) : query.isError ? (
              <ErrorState
                title={t('common:states.errorTitle')}
                description={errorMessage(query.error, t)}
                traceId={errorTraceId(query.error)}
                retryLabel={t('common:actions.retry')}
                onRetry={() => void query.refetch()}
                traceCopyLabel={t('common:actions.copy')}
                traceCopiedLabel={t('common:actions.copied')}
              />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<BedDouble className="ico ico-24" aria-hidden="true" />}
                title={
                  hasFilters ? t('reservations:emptySearchTitle') : t('reservations:emptyTitle')
                }
                description={
                  hasFilters ? t('reservations:emptySearchBody') : t('reservations:emptyBody')
                }
                action={
                  <Can permission="reservation.create">
                    <Button variant="primary" asChild>
                      <Link to="/reservations/new">{t('reservations:create.action')}</Link>
                    </Button>
                  </Can>
                }
              />
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('reservations:columns.reference')}</Th>
                      <Th>{t('reservations:columns.guest')}</Th>
                      <Th>{t('reservations:columns.resource')}</Th>
                      <Th>{t('reservations:columns.dates')}</Th>
                      <Th align="right">{t('reservations:columns.nights')}</Th>
                      <Th align="right">{t('reservations:columns.total')}</Th>
                      <Th align="right">{t('reservations:columns.balance')}</Th>
                      <Th>{t('reservations:columns.status')}</Th>
                      <Th>{t('reservations:columns.compliance')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <Tr key={row.id}>
                        <Td>
                          <Link
                            to="/reservations/$reservationId"
                            params={{ reservationId: row.id }}
                          >
                            <span className="cell-id">{row.reference}</span>
                          </Link>
                        </Td>
                        <Td>
                          <UserCell
                            avatar={<Avatar name={row.guest.displayName} size="sm" />}
                            name={row.guest.displayName}
                            subtitle={
                              row.billingParty.id !== row.guest.id
                                ? t('reservations:list.billedTo', {
                                    name: row.billingParty.displayName,
                                  })
                                : undefined
                            }
                          />
                        </Td>
                        <Td>
                          <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                            <span className="res-tag">{row.resource.internalRef ?? '—'}</span>
                            <span className="t-13 truncate">{row.resource.name}</span>
                          </div>
                        </Td>
                        <Td>
                          <div className="t-13" style={{ whiteSpace: 'nowrap' }}>
                            {formatDate(row.arrivalAt, i18n.language)}
                          </div>
                          <div className="t-12 muted" style={{ whiteSpace: 'nowrap' }}>
                            → {formatDate(row.departureAt, i18n.language)}
                          </div>
                        </Td>
                        <Td align="right" numeric>
                          {row.nights ?? '—'}
                        </Td>
                        <Td align="right" numeric>
                          {formatAmount(row.totals.net, i18n.language)}
                        </Td>
                        <Td align="right" numeric>
                          {row.totals.balance > 0 ? (
                            <span className="strong" style={{ color: 'var(--warning)' }}>
                              {formatAmount(row.totals.balance, i18n.language)}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--success)' }}>
                              {formatAmount(0, i18n.language)}
                            </span>
                          )}
                        </Td>
                        <Td>
                          <StatusBadge status={reservationTone(row.status)}>
                            {t(`reservations:status.${row.status}`)}
                          </StatusBadge>
                        </Td>
                        <Td>
                          <Badge tone={complianceTone(row.compliance.status)}>
                            {t(`reservations:compliance.${row.compliance.status}`)}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </CardBody>

          {query.hasNextPage ? (
            <div className="card__foot">
              <Button
                variant="secondary"
                size="sm"
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {t('common:pagination.loadMore')}
              </Button>
            </div>
          ) : null}
        </Card>
      </div>
    </Container>
  );
}
